import React from "react";
import { Badge } from "@/components/ui/badge";

/**
 * @typedef {'low'|'medium'|'high'} RiskLevel   - Notices vocabulary
 * @typedef {'ok'|'warning'|'critical'} Severity - AISIngestion vocabulary
 *
 * Unified 3-band color component that replaces two diverged inline constants:
 *   - RISK_COLOR in Notices.jsx (text-color only)
 *   - SEVERITY_STYLES in AISIngestion.jsx (bg+text+border)
 * Both mapped to the same emerald/amber/rose semantic.
 * Color is always paired with a text label (never color-only).
 *
 * @typedef {Object} RiskBadgeProps
 * @property {RiskLevel} [level]        - Notices vocabulary (low/medium/high)
 * @property {Severity}  [severity]     - AISIngestion vocabulary (ok/warning/critical)
 * @property {'badge'|'text'} [variant] - 'badge' = styled Badge; 'text' = colored span
 * @property {string}    [className]
 */

const BANDS = {
  low:      { text: "text-emerald-700", bg: "bg-emerald-500/10", border: "border-emerald-300" },
  ok:       { text: "text-emerald-700", bg: "bg-emerald-500/10", border: "border-emerald-300" },
  medium:   { text: "text-amber-700",   bg: "bg-amber-500/10",   border: "border-amber-300"   },
  warning:  { text: "text-amber-700",   bg: "bg-amber-500/10",   border: "border-amber-300"   },
  high:     { text: "text-rose-700",    bg: "bg-rose-500/10",    border: "border-rose-300"    },
  critical: { text: "text-rose-700",    bg: "bg-rose-500/10",    border: "border-rose-300"    },
};

/**
 * Returns bg+text+border className string for container-level severity coloring
 * (replaces SEVERITY_STYLES constant lookups at the call site).
 * @param {string} key - level or severity value
 * @returns {string}
 */
export function riskClasses(key) {
  const b = BANDS[key?.toLowerCase()] || BANDS.ok;
  return `${b.bg} ${b.text} ${b.border}`;
}

/**
 * Returns only the text-color class (for inline text usage where bg/border are unwanted).
 * @param {string} key - level or severity value
 * @returns {string}
 */
export function riskTextClass(key) {
  const b = BANDS[key?.toLowerCase()] || BANDS.ok;
  return b.text;
}

/** @param {RiskBadgeProps} props */
export default function RiskBadge({ level, severity, variant = "badge", className = "" }) {
  const key = (level || severity || "").toLowerCase();
  const b = BANDS[key] || BANDS.ok;

  if (variant === "text") {
    return <span className={`capitalize ${b.text} ${className}`}>{key}</span>;
  }

  return (
    <Badge
      variant="outline"
      className={`${b.bg} ${b.text} ${b.border} capitalize ${className}`}
    >
      {key}
    </Badge>
  );
}
